import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Package, Tag as TagIcon, ChevronLeft, ChevronRight, Store, Zap } from 'lucide-react';
import { Button, Input, useToast, Card } from '@caruso/ui';
import { productsService, Product } from '@/services/products.service';
import { QuickSaleModal } from '@/components/quick-sale-modal';

export function Products() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [quickSaleModal, setQuickSaleModal] = useState<{
    open: boolean;
    productId: string;
    productName: string;
  }>({
    open: false,
    productId: '',
    productName: '',
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = useQuery({
    queryKey: ['products', search, categoryFilter, page],
    queryFn: () =>
      productsService.getAll({
        search: search || undefined,
        category: categoryFilter || undefined,
        page,
        limit: 12,
      }),
  });

  const { data: categories } = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => productsService.getCategories(),
  });

  const deleteMutation = useMutation({
    mutationFn: productsService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast({
        title: 'Produto excluído!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao excluir produto',
        variant: 'destructive',
      });
    },
  });

  const toggleStoreMutation = useMutation({
    mutationFn: productsService.toggleStore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast({
        title: 'Visibilidade na loja atualizada!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao atualizar visibilidade',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir este produto?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleStore = (id: string) => {
    toggleStoreMutation.mutate(id);
  };

  const formatPrice = (price?: number) => {
    if (!price) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-brand-gold">Produtos</h1>
          <p className="text-brand-ivory/60 mt-1 text-sm sm:text-base">
            Gerencie seu catálogo de produtos
          </p>
        </div>
        <Button onClick={() => navigate('/app/produtos/novo')} variant="premium" className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
            <Input
              placeholder="Buscar produtos..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-brand-smoke/50 text-brand-ivory border border-brand-gold/20 rounded-lg"
          >
            <option value="">Todas as categorias</option>
            {categories?.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          {(search || categoryFilter) && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setCategoryFilter('');
                setPage(1);
              }}
            >
              Limpar Filtros
            </Button>
          )}
        </div>
      </Card>

      {/* Products List/Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : data?.data.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-brand-gold/40 mx-auto mb-4" />
          <p className="text-brand-ivory/50 mb-4">
            {search || categoryFilter
              ? 'Nenhum produto encontrado com os filtros aplicados'
              : 'Nenhum produto encontrado'}
          </p>
          <Button onClick={() => navigate('/app/produtos/novo')} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Adicionar primeiro produto
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {data?.data.map((product: Product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <Card
                  className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all overflow-hidden h-full flex flex-col cursor-pointer"
                  onClick={() => navigate(`/app/produtos/${product.id}`)}
                >
                  {/* Image */}
                  <div className="aspect-square bg-brand-smoke overflow-hidden">
                    {product.images.length > 0 ? (
                      <img
                        src={product.images.find((img) => img.isPrimary)?.url || product.images[0].url}
                        alt={product.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-brand-gold/20" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-serif text-brand-gold text-lg line-clamp-1 flex-1">
                        {product.name}
                      </h3>
                      {!product.active && (
                        <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded ml-2">
                          Inativo
                        </span>
                      )}
                    </div>

                    {product.category && (
                      <div className="flex items-center gap-1 text-xs text-brand-ivory/60 mb-2">
                        <TagIcon className="w-3 h-3" />
                        {product.category}
                      </div>
                    )}

                    {product.description && (
                      <p className="text-sm text-brand-ivory/60 line-clamp-2 mb-3">
                        {product.description}
                      </p>
                    )}

                    <div className="mt-auto">
                      <div className="mb-4">
                        <p className="text-2xl font-bold text-brand-gold">{formatPrice(product.price)}</p>
                        {product.stock !== undefined && (
                          <p className="text-xs text-brand-ivory/50 mt-1">
                            Estoque: {product.stock} {product.stock === 1 ? 'unidade' : 'unidades'}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-brand-gold/30 hover:bg-brand-gold/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setQuickSaleModal({
                              open: true,
                              productId: product.id,
                              productName: product.name,
                            });
                          }}
                        >
                          <Zap className="w-4 h-4 mr-1" />
                          Venda Rápida
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStore(product.id);
                          }}
                          disabled={toggleStoreMutation.isPending}
                          className={product.showInStore ? 'text-green-400' : 'text-brand-ivory/40'}
                          title={product.showInStore ? 'Visível na loja' : 'Oculto na loja'}
                        >
                          <Store className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/app/produtos/${product.id}`);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(product.id);
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {data && data.meta.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-4 text-brand-ivory">
                Página {page} de {data.meta.totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page + 1)}
                disabled={page === data.meta.totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Quick Sale Modal */}
      <QuickSaleModal
        open={quickSaleModal.open}
        onOpenChange={(open) =>
          setQuickSaleModal((prev) => ({ ...prev, open }))
        }
        productId={quickSaleModal.productId}
        productName={quickSaleModal.productName}
      />
    </div>
  );
}
